import { useEffect, useRef, useCallback } from 'react';
import { toast } from '@/components/ui/use-toast';
import type { Migration, MigrationStatus, MigrationLog } from '@/types';
import type { ProgressUpdate } from '@/hooks/useMigrationProgress';

interface ProgressNotificationsProps {
  migration: Migration;
  progress: ProgressUpdate | null;
  logs: MigrationLog[];
  enableBrowserNotifications?: boolean | undefined;
}

const originalTitle = typeof document !== 'undefined' ? document.title : '';

export function ProgressNotifications({
  migration,
  progress,
  logs,
  enableBrowserNotifications = true,
}: ProgressNotificationsProps) {
  const previousStatusRef = useRef<MigrationStatus>(migration.status);

  // Pre-seed with modules already in a terminal state at mount time so that
  // navigating to a finished migration doesn't re-fire toasts for past events.
  const notifiedModulesRef = useRef<Set<string>>(
    new Set(
      migration.modules
        .filter((m) => m.migrateStatus === 'Succeeded' || m.migrateStatus === 'Failed')
        .map((m) => `${m.moduleKey}-${m.migrateStatus}`),
    ),
  );
  const notifiedErrorsRef = useRef<Set<string>>(new Set());

  // Request browser notification permission
  useEffect(() => {
    if (enableBrowserNotifications && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [enableBrowserNotifications]);

  // Update tab title with progress
  useEffect(() => {
    if (migration.status === 'InProgress' && progress) {
      document.title = `(${Math.round(progress.percentage)}%) Migration - ${migration.organizationName}`;
    } else if (migration.status === 'Succeeded') {
      document.title = `✓ Complete - ${migration.organizationName}`;
    } else if (migration.status === 'Failed') {
      document.title = `✗ Failed - ${migration.organizationName}`;
    } else if (migration.status === 'Partial') {
      document.title = `⚠ Partial - ${migration.organizationName}`;
    }

    return () => {
      document.title = originalTitle;
    };
  }, [migration.status, migration.organizationName, progress]);

  // Show browser notification
  const showBrowserNotification = useCallback(
    (title: string, body: string) => {
      if (!enableBrowserNotifications) return;
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;

      // Don't show if tab is visible
      if (document.visibilityState === 'visible') return;

      new Notification(title, {
        body,
        tag: 'migration-notification',
      });
    },
    [enableBrowserNotifications]
  );

  // Handle migration completion
  useEffect(() => {
    if (previousStatusRef.current === 'InProgress' && migration.status !== 'InProgress') {
      const status = migration.status;

      if (status === 'Succeeded') {
        toast({
          title: 'Migration Complete',
          description: `${migration.organizationName} has been successfully migrated.`,
          variant: 'success',
        });
        showBrowserNotification(
          'Migration Complete',
          `${migration.organizationName} has been successfully migrated.`
        );
      } else if (status === 'Failed') {
        toast({
          title: 'Migration Failed',
          description: `The migration for ${migration.organizationName} failed.`,
          variant: 'destructive',
        });
        showBrowserNotification(
          'Migration Failed',
          `The migration for ${migration.organizationName} failed.`
        );
      } else if (status === 'Partial') {
        toast({
          title: 'Migration Partially Complete',
          description: `Some modules for ${migration.organizationName} failed to migrate.`,
          variant: 'warning',
        });
        showBrowserNotification(
          'Migration Partially Complete',
          `Some modules for ${migration.organizationName} failed.`
        );
      }
    }

    previousStatusRef.current = migration.status;
  }, [migration.status, migration.organizationName, showBrowserNotification]);

  // Handle module completions
  useEffect(() => {
    for (const mod of migration.modules) {
      const key = `${mod.moduleKey}-${mod.migrateStatus}`;

      if (notifiedModulesRef.current.has(key)) continue;

      if (mod.migrateStatus === 'Succeeded') {
        notifiedModulesRef.current.add(key);
        toast({
          title: `${mod.moduleKey} migrated`,
          description: 'Module migration completed successfully.',
          variant: 'success',
        });
      } else if (mod.migrateStatus === 'Failed') {
        notifiedModulesRef.current.add(key);
        toast({
          title: `${mod.moduleKey} failed`,
          description: mod.migrateError ?? 'Module migration failed.',
          variant: 'destructive',
        });
      }
    }
  }, [migration.modules]);

  // Handle error logs
  useEffect(() => {
    for (const log of logs) {
      if (log.level !== 'Error') continue;
      if (notifiedErrorsRef.current.has(log.id)) continue;

      notifiedErrorsRef.current.add(log.id);

      toast({
        title: log.moduleKey ? `Error in ${log.moduleKey}` : 'Migration Error',
        description: log.message,
        variant: 'destructive',
      });
    }
  }, [logs]);

  // This component doesn't render anything visible
  return null;
}
